import {
  ProcessedImageData,
  EscPosImageOptions,
  EscPosImageLoadResult,
} from './esc-pos-image.types';
import { ImageProcessor } from './image-processor';
import { ImageLoader } from './image-loader';

/**
 * EscPosImage - Encapsulates async image loading and processing for ESC/POS printing
 *
 * @example
 * // Basic usage - respects original size
 * const image = new EscPosImage('/assets/logo.png');
 * await image.load();
 * encoder.image(image);
 *
 * // With specific dimensions
 * const image = new EscPosImage('/assets/banner.jpg', {
 *   width: 400,
 *   height: 200,
 *   algorithm: 'floydsteinberg'
 * });
 *
 * // Proportional width, with character limit as safety
 * const image = new EscPosImage('/assets/photo.jpg', {
 *   width: 500,  // Scales to 500px width
 *   maxCharactersPerLine: 32  // But limits to 384px max (32*12)
 * });
 * // Result: 384px width (limited by maxCharactersPerLine)
 *
 * // Original size with character limit and center alignment
 * const image = new EscPosImage('/assets/wide-image.jpg', {
 *   maxCharactersPerLine: 48,  // Limits to 576px max
 *   alignment: 'center'        // Center the image
 * });
 * // Result: If original > 576px, scales down proportionally and centers
 */
export class EscPosImage {
  private imageUrl: string;
  private options: Required<EscPosImageOptions>;
  private loaded: boolean = false;
  private loadResult?: EscPosImageLoadResult;
  private htmlImageElement?: HTMLImageElement;

  static async loadFromUrl(
    imageUrl: string,
    options: EscPosImageOptions = {},
  ): Promise<EscPosImage> {
    const image = new EscPosImage(imageUrl, options);

    await image.load();

    return image;
  }

  constructor(imageUrl: string, options: EscPosImageOptions = {}) {
    this.imageUrl = imageUrl;

    // Set defaults
    this.options = {
      algorithm: options.algorithm || 'threshold',
      threshold: options.threshold || 128,
      maxCharactersPerLine: options.maxCharactersPerLine,
      width: options.width,
      height: options.height,
      alignment: options.alignment || 'left',
    };
  }

  /**
   * Load and process the image asynchronously
   * This method handles image loading, dimension calculations, and dithering
   */
  async load(): Promise<void> {
    if (this.loaded) {
      return; // Already loaded
    }

    try {
      // Load image from URL
      this.htmlImageElement = await ImageLoader.loadFromUrl(this.imageUrl);

      // Get original dimensions
      const originalDimensions = {
        width: this.htmlImageElement.naturalWidth,
        height: this.htmlImageElement.naturalHeight,
      };

      // Calculate target dimensions
      const targetDimensions = this._calculateTargetDimensions(originalDimensions);

      // Process image with dithering algorithm
      const processor = new ImageProcessor();

      const processedImageData = await processor.processAsync(
        {
          width: targetDimensions.width,
          height: targetDimensions.height,
          algorithm: this.options.algorithm,
          options: { threshold: this.options.threshold },
        },
        this.htmlImageElement,
      );

      // Store results
      this.loadResult = {
        originalDimensions,
        targetDimensions,
        processedImageData,
      };

      // Cleanup processor
      processor.dispose();

      this.loaded = true;
    } catch (error) {
      throw new Error(`Failed to load EscPosImage from '${this.imageUrl}': ${error.message}`);
    }
  }

  /**
   * Check if the image has been loaded and processed
   */
  isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Get processed image data (only available after load())
   * @throws Error if image is not loaded
   */
  getProcessedImageData(): ProcessedImageData {
    if (!this.loaded || !this.loadResult) {
      throw new Error(
        'Image must be loaded first. Call await load() before accessing processed data.',
      );
    }
    return this.loadResult.processedImageData;
  }

  /**
   * Get original image dimensions (only available after load())
   * @throws Error if image is not loaded
   */
  getOriginalDimensions(): { width: number; height: number } {
    if (!this.loaded || !this.loadResult) {
      throw new Error('Image must be loaded first. Call await load() before accessing dimensions.');
    }
    return this.loadResult.originalDimensions;
  }

  /**
   * Get target (processed) image dimensions (only available after load())
   * @throws Error if image is not loaded
   */
  getTargetDimensions(): { width: number; height: number } {
    if (!this.loaded || !this.loadResult) {
      throw new Error('Image must be loaded first. Call await load() before accessing dimensions.');
    }
    return this.loadResult.targetDimensions;
  }

  /**
   * Get image configuration options
   */
  getOptions(): Required<EscPosImageOptions> {
    return { ...this.options };
  }

  /**
   * Get image URL
   */
  getImageUrl(): string {
    return this.imageUrl;
  }

  /**
   * Calculate target dimensions based on options
   * Process: 1) Apply width/height settings, 2) Apply maxCharactersPerLine limit if needed
   */
  private _calculateTargetDimensions(original: { width: number; height: number }): {
    width: number;
    height: number;
  } {
    let targetWidth = original.width;
    let targetHeight = original.height;

    // STEP 1: Apply explicit dimensions if configured
    if (this.options.width && this.options.height) {
      // Both width and height specified - use exact dimensions
      targetWidth = this.options.width;
      targetHeight = this.options.height;
    } else if (this.options.width) {
      // Only width specified - calculate proportional height
      const scaleFactor = this.options.width / original.width;
      targetWidth = this.options.width;
      targetHeight = Math.round(original.height * scaleFactor);
    } else if (this.options.height) {
      // Only height specified - calculate proportional width
      const scaleFactor = this.options.height / original.height;
      targetWidth = Math.round(original.width * scaleFactor);
      targetHeight = this.options.height;
    }
    // If no explicit dimensions, keep original dimensions (will be limited by maxCharactersPerLine if set)

    // STEP 2: Apply maxCharactersPerLine as final width limit (if configured)
    if (this.options.maxCharactersPerLine) {
      // Convert characters to pixels: 1 character ≈ 12 pixels
      const maxWidthInPixels = this.options.maxCharactersPerLine * 12;

      // If current width exceeds the character limit, scale down proportionally
      if (targetWidth > maxWidthInPixels) {
        const scaleFactor = maxWidthInPixels / targetWidth;
        targetWidth = maxWidthInPixels;
        targetHeight = Math.round(targetHeight * scaleFactor);
      }
    }

    // STEP 3: Adjust to ESC/POS requirements (multiple of 8)
    targetWidth = Math.floor(targetWidth / 8) * 8;
    targetHeight = Math.floor(targetHeight / 8) * 8;

    // STEP 4: Ensure minimum dimensions
    if (targetWidth < 8) targetWidth = 8;
    if (targetHeight < 8) targetHeight = 8;

    return { width: targetWidth, height: targetHeight };
  }

  /**
   * Create a copy of this EscPosImage with different options
   * The new instance will need to be loaded separately
   */
  clone(newOptions: Partial<EscPosImageOptions> = {}): EscPosImage {
    const mergedOptions = { ...this.options, ...newOptions };
    return new EscPosImage(this.imageUrl, mergedOptions);
  }

  /**
   * Reset the loaded state (forces reload on next load() call)
   */
  reset(): void {
    this.loaded = false;
    this.loadResult = undefined;
    this.htmlImageElement = undefined;
  }
}
